/**
 * @file Renders one tier's checkbox label and, when the tier is selected,
 * the amount input that updates that tier's entry in the form.
 */
import { Form, InputNumber, Typography, Space } from "antd";

import { W0 } from "@components/Constants";
import { getAmountWithoutCents } from "@components/Amount";

import type { SubInvestment } from "../../helpers/types";
import type { Form as FormInstance, Tier } from "../types";

const { Text } = Typography;

export const KEY_AMOUNT = "amount";
export const KEY_AMOUNTS_MAP = "_amountsMap";

const LABEL_CLASS = "Class";
const LABEL_REQUIRED = "Field is required";

const MAX_AMOUNT = 100000000;
const CENTS_IN_DOLLAR = 100;
const INPUT_WIDTH = 160;

const THOUSANDS_REGEX = /\B(?=(\d{3})+(?!\d))/g;
const CURRENCY_REGEX = /\$\s?|(,*)/g;

const OPTION_STYLE = { lineHeight: 1.2 };
const ITEM_STYLE = { marginBottom: 0, marginTop: W0 / 2 };
const INPUT_STYLE = { width: INPUT_WIDTH };

const RULES = [{ required: true, message: LABEL_REQUIRED }];

type Props = {
  name: string[];
  form: FormInstance;
  path: string[];
  tier: Tier;
  autoFocus: boolean;
  selectedTierIds: string[];
};

/**
 * Renders a tier's label, description and, when the tier is selected, the
 * amount input for that tier.
 */
const InvestmentTierOption = ({
  name,
  form,
  path,
  tier,
  autoFocus,
  selectedTierIds,
}: Props) => {
  const { title, name: id } = tier;

  /** Adds thousands separators to the displayed amount. */
  const formatter = (value?: string | number) => `${value}`.replace(THOUSANDS_REGEX, ",");

  /** Stores the changed amount, in cents, on this tier's investment entry. */
  const onChange = (value: number | null) => {
    let amount = value;

    if (amount) {
      amount = getAmountWithoutCents(amount);
    }

    const investments: Partial<SubInvestment>[] = form.getFieldValue(path);

    const tierIndex = investments.findIndex((investment) => investment.projectTierId === id);
    const isTierFound = tierIndex >= 0;

    let investmentsChanged = [ ...investments ];

    if (isTierFound) {
      investmentsChanged[tierIndex].amount = amount;
    } else {
      investmentsChanged = [ ...investmentsChanged, { projectTierId: id, amount } ];
    }

    form.setFieldValue(path, investmentsChanged);
  };

  const label = title || `${LABEL_CLASS} ${id}`;

  const amountsFieldName = [ ...name.slice(0, -1), KEY_AMOUNTS_MAP, id, KEY_AMOUNT ];

  /** Converts the stored cents amount to dollars for display. */
  const getValueProps = (value: number) => {
    if (!value) {
      return { value };
    }

    return { value: value / CENTS_IN_DOLLAR };
  };

  /** Strips the currency symbol and separators from the typed value. */
  const parser = (value?: string) => value.replace(CURRENCY_REGEX, "");

  return (
    <Space
      size={0}
      style={OPTION_STYLE}
      direction="vertical"
    >
      <Text className="checkbox-label">
        { label }
      </Text>

      <Text type="secondary">
        {tier.description}
      </Text>

      {
        selectedTierIds.includes(id) && (
          <Form.Item
            name={amountsFieldName}
            style={ITEM_STYLE}
            rules={RULES}
            getValueProps={getValueProps}
          >
            <InputNumber
              max={MAX_AMOUNT}
              min={0}
              style={INPUT_STYLE}
              prefix="$"
              parser={parser}
              controls={false}
              onChange={onChange}
              formatter={formatter}
              autoFocus={autoFocus}
            />
          </Form.Item>
        )
      }
    </Space>
  );
};

export default InvestmentTierOption;
